import { CommentsCount } from "@/components/comments/CommentsCount.tsx";
import { DataBars, DataBarsRow } from "@/components/data-bars/DataBars";
import { FormAggregatedResultElementProps } from "@/components/form-result/aggregated-result/components/types.ts";
import { Box, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const FormAggregatedResultGoalElement = ({
  element,
  result,
  showDetailModalCallback,
}: FormAggregatedResultElementProps) => {
  const goalsSettings = element.goals_settings;
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!goalsSettings || !result || !result.goals_data) return null;
    const scaleFirst = goalsSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);

    let sum = 0;
    for (const option of scaleFirst) {
      const selectOption = result.goals_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      sum += selectOption?.count || 0;
    }

    const rowsInner: DataBarsRow[] = [];
    for (const option of scaleFirst) {
      const selectOption = result.goals_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;
      rowsInner.push({
        title: option.title,
        emoji: option.emoji,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
        onClick: showDetailModalCallback
          ? () =>
              showDetailModalCallback(element, {
                answer_scale_option_ids: [option.id],
              })
          : undefined,
      });
    }
    return rowsInner;
  }, [result]);

  const rows2 = useMemo(() => {
    if (!goalsSettings || !result || !result.goals_data) return null;
    const scaleSecond = goalsSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);
    if (!scaleSecond) return null;

    let sum = 0;
    for (const option of scaleSecond) {
      const selectOption = result.goals_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      sum += selectOption?.count || 0;
    }

    const rowsInner: DataBarsRow[] = [];
    for (const option of scaleSecond) {
      const selectOption = result.goals_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;
      rowsInner.push({
        title: option.title,
        emoji: option.emoji,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
        onClick: showDetailModalCallback
          ? () =>
              showDetailModalCallback(element, {
                answer_scale_option_ids: [option.id],
              })
          : undefined,
      });
    }
    return rowsInner;
  }, [result]);

  if (!goalsSettings) return null;

  return (
    <Box
      className={showDetailModalCallback ? "cursor-pointer" : undefined}
      onClick={showDetailModalCallback ? () => showDetailModalCallback(element) : undefined}
    >
      <Title
        header={goalsSettings.title || t("goals:goal")}
        size={"sm"}
        className={goalsSettings.title ? "" : "text-secondary"}
        required={goalsSettings.is_required}
      />
      {result && result.goals_data && result.goals_data.comments_count > 0 && (
        <Box className={"py-4"}>
          <CommentsCount
            totalComments={result.goals_data.comments_count}
            commentersPreview={result.goals_data.commenters_preview || []}
          />
        </Box>
      )}
      {rows && <DataBars rows={rows} />}
      {rows2 && <DataBars rows={rows2} />}
      {!result && (element?.goals ?? []).length === 0 && (
        <Text className={"text-secondary text-sm"}>
          {t("performance_review:no_goals_to_evaluate")}
        </Text>
      )}
    </Box>
  );
};
